import { Component } from "@theme/component";

const WINDOW_CONFIG = (/** @type {any} */ (window)).themeConfig?.colorSwatches || {};
const RESOLVED_SWATCHES_CONFIG = {
    requestLimit: Number(WINDOW_CONFIG.requestLimit || (/** @type {any} */ (window)).swatchesRequestLimit || 250),
    totalProducts: Number(WINDOW_CONFIG.totalProducts || (/** @type {any} */ (window)).totalProducts || 0),
    showOnlyAvailable: typeof WINDOW_CONFIG.showOnlyAvailable === "boolean" ? WINDOW_CONFIG.showOnlyAvailable : undefined,
};

/**
 * @typedef {object} SwatchProduct
 * @property {string} url
 * @property {string} [product_color_hex]
 * @property {string} [product_color_label]
 * @property {string} [color_code]
 * @property {string} [color_code_current]
 * @property {string} [product_image]
 * @property {string} [product_model_image]
 * @property {boolean} [product_color_is_white]
 * @property {boolean} [available]
 */

class ColorSwatchesStore {
    static #DEFAULT_LIMIT = 250;
    static #CACHE_KEY_PREFIX = "colorSwatchesData";
    static #CACHE_TTL_MS = 60 * 60 * 1000;
    static #COLLECTION_PATH = "collections/all";
    static #MAX_RETRIES = 1;

    /** @type {Promise<SwatchProduct[]> | null} */
    static #promise = null;

    /** @type {SwatchProduct[] | null} */
    static #products = null;

    /** @type {Map<string, SwatchProduct[]> | null} */
    static #productMap = null;

    static get #limit() {
        return RESOLVED_SWATCHES_CONFIG.requestLimit || this.#DEFAULT_LIMIT;
    }

    static get #totalProducts() {
        return RESOLVED_SWATCHES_CONFIG.totalProducts;
    }

    static get #locale() {
        const locale =
            document.documentElement.lang ||
            (/** @type {any} */ (window)).Shopify?.locale ||
            "default";

        return String(locale).trim().toLowerCase() || "default";
    }

    static get #cacheKey() {
        return `${this.#CACHE_KEY_PREFIX}:${this.#locale}`;
    }

    /** @returns {SwatchProduct[] | Promise<SwatchProduct[]>} */
    static getProducts() {
        if (this.#products) return this.#products;
        if (this.#promise) return this.#promise;

        const cached = this.getCached();
        if (cached) return cached;

        this.#promise ??= this.#fetchAll().finally(() => {
            this.#promise = null;
        });

        return this.#promise;
    }

    static async #fetchAll() {
        const limit = this.#limit;
        const totalPages = Math.ceil(this.#totalProducts / limit);

        if (totalPages === 0) {
            return [];
        }

        const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
        const results = await Promise.all(
            pages.map((page) => this.#fetchPage(page))
        );

        const failedPages = results.filter((result) => result.status === "rejected");
        const successfulPages = results.filter((result) => result.status === "fulfilled");

        if (successfulPages.length === 0) {
            const firstFailure = failedPages[0];
            const reason = firstFailure?.error?.message ? ` First error: ${firstFailure.error.message}` : "";
            throw new Error(`Failed to load ${failedPages.length} swatch data page(s).${reason}`);
        }

        if (failedPages.length > 0) {
            console.warn(
                `[ColorSwatchesStore] Failed to load ${failedPages.length} swatch data page(s); using partial swatch data.`,
                failedPages.map((result) => result.page)
            );
        }

        const products = successfulPages.flatMap((result) => result.products);

        this.#products = products;
        this.#buildMap(products);

        this.#setCache(products);

        return products;
    }

    /**
     * @param {number} page
     * @returns {Promise<
     *   | { status: "fulfilled"; page: number; products: SwatchProduct[] }
     *   | { status: "rejected"; page: number; error: Error }
     * >}
     */
    static async #fetchPage(page) {
        let lastError = null;

        for (let attempt = 0; attempt <= this.#MAX_RETRIES; attempt += 1) {
            try {
                const url = this.#buildPageUrl(page);
                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const payload = await response.json();
                const products = Array.isArray(payload?.products) ? payload.products : [];

                return {
                    status: "fulfilled",
                    page,
                    products,
                };
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
            }
        }

        return {
            status: "rejected",
            page,
            error: lastError ?? new Error("Unknown swatch data error."),
        };
    }

    /** @param {number} page */
    static #buildPageUrl(page) {
        const root =
            Theme?.routes?.root_url ||
            (/** @type {any} */ (window)).Shopify?.routes?.root ||
            "/";
        const normalizedRoot = root.endsWith("/") ? root : `${root}/`;
        const url = new URL(this.#COLLECTION_PATH, window.location.origin + normalizedRoot);

        url.searchParams.set("view", "color-swatches");
        url.searchParams.set("page", String(page));

        return url.toString();
    }

    static getCached() {
        if (this.#products) return this.#products;

        try {
            const raw = localStorage.getItem(this.#cacheKey);
            const parsed = raw ? JSON.parse(raw) : null;

            if (!parsed || !Array.isArray(parsed.products) || typeof parsed.expiresAt !== "number") {
                localStorage.removeItem(this.#cacheKey);
                return null;
            }

            if (parsed.expiresAt <= Date.now()) {
                localStorage.removeItem(this.#cacheKey);
                return null;
            }

            this.#products = parsed.products;
            this.#buildMap(parsed.products);

            return parsed.products;
        } catch {
            console.warn("[ColorSwatchesStore] Unable to parse cached swatch data from localStorage.");
            return null;
        }
    }

    /** @param {SwatchProduct[]} products */
    static #buildMap(products) {
        const map = new Map();
        for (const product of products) {
            const key = product.color_code;
            if (key == null) continue;
            const group = map.get(key);
            if (group) {
                group.push(product);
            } else {
                map.set(key, [product]);
            }
        }
        this.#productMap = map;
    }

    /** @param {string | undefined} colorCode */
    static getByColorCode(colorCode) {
        if (!colorCode) return [];
        return this.#productMap?.get(colorCode) ?? [];
    }

    static hasProducts() {
        return Array.isArray(this.#products);
    }

    /** @param {SwatchProduct[]} products */
    static #setCache(products) {
        try {
            localStorage.setItem(this.#cacheKey, JSON.stringify({
                products,
                expiresAt: Date.now() + this.#CACHE_TTL_MS,
            }));
        } catch {
            console.warn("[ColorSwatchesStore] Unable to cache swatch data in localStorage.");
        }
    }
}

export default class ColorSwatches extends Component {
    requiredRefs = ["swatches"];

    /** @type {AbortController | null} */
    #abortController = null;
    #visibleElementsCard = 4;
    #visibleElementsPdp = 4;
    #config = RESOLVED_SWATCHES_CONFIG;

    get #isCard() {
        return !!this.closest('[product-card]') || !!this.closest('#quick-add-dialog');
    }

    connectedCallback() {
        super.connectedCallback();
        this.dataset.context = this.#isCard ? 'card' : 'pdp';
        this.#abortController = new AbortController();

        if (ColorSwatchesStore.hasProducts()) {
            this.#render();
            return;
        }

        this.#setup();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
    }

    updatedCallback() {
        if (!ColorSwatchesStore.hasProducts()) return;

        this.#render();
    }

    async #setup() {
        try {
            await ColorSwatchesStore.getProducts();

            if (this.#abortController?.signal.aborted) return;

            this.#render();
        } catch (error) {
            console.error("[ColorSwatches] Failed to load swatch data:", error);
        }
    }

    get #showOnlyAvailable() {
        if (typeof this.#config.showOnlyAvailable === "boolean") {
            return this.#config.showOnlyAvailable;
        }

        return this.dataset.showOnlyAvailableColors === "true";
    }

    #render() {
        const swatchesList = /** @type {HTMLElement | undefined} */ (this.refs.swatches);

        if (!swatchesList || swatchesList.classList.contains("is-loaded")) return;

        const { colorCode, currentCode } = this.dataset;
        let items = ColorSwatchesStore.getByColorCode(colorCode);

        if (this.#showOnlyAvailable) {
            items = items.filter((product) => product.available === true);
        }

        if (items.length <= 1) return;

        const activeIndex = items.findIndex((item) => item.color_code_current === currentCode);
        const orderedItems = activeIndex > 0
            ? [items[activeIndex], ...items.slice(0, activeIndex), ...items.slice(activeIndex + 1)]
            : items;

        const hasOnlyImageSwatches = !this.#isCard && orderedItems.every((item) => !!item.product_image);
        const limit = this.#isCard
            ? this.#visibleElementsCard
            : hasOnlyImageSwatches
                ? Infinity
                : this.#visibleElementsPdp;

        let html = orderedItems.map((item, index) => {
            const hiddenClass = index >= limit ? " swatch-variant--hidden" : "";
            const activeClass = item.color_code_current === currentCode ? " active" : "";
            return this.#swatchHtml(item, hiddenClass + activeClass);
        }).join("");
        if (this.#isCard && items.length > limit) {
            const moreHref = orderedItems[0]?.url ?? '#';
            html += `<a href="${moreHref}" class="swatch-variant-more">+${items.length - limit}</a>`;
        } else if (!this.#isCard && items.length > limit) {
            html += this.#showMoreButtonHtml();
        }

        swatchesList.innerHTML = html;
        swatchesList.classList.add("is-loaded");

        this.#bindEvents();
        this.#updateScrollFade();

        document.dispatchEvent(new CustomEvent("color-swatches-loaded"));
    }

    #updateScrollFade() {
        const swatchesList = /** @type {HTMLElement | undefined} */ (this.refs.swatches);
        if (!swatchesList) return;

        const canScroll = swatchesList.scrollWidth > swatchesList.clientWidth + 1;
        const atEnd = swatchesList.scrollLeft + swatchesList.clientWidth >= swatchesList.scrollWidth - 1;
        const atStart = swatchesList.scrollLeft <= 1;

        this.classList.toggle("color-swatches--scrollable", canScroll && !atEnd);
        this.classList.toggle("color-swatches--scrolled", canScroll && !atStart);
    }

    /** @returns {HTMLImageElement | null} */
    #findCardImage() {
        const card = this.closest('[product-card]');

        return card?.querySelector('[ref="cardGallery"] img') ?? null;
    }

    #bindHoverEvents() {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        const swatchesList = /** @type {HTMLElement | undefined} */ (this.refs.swatches);
        if (!swatchesList) return;

        const signal = /** @type {AbortController} */ (this.#abortController).signal;

        swatchesList.addEventListener('mouseenter', (e) => {
            const li = /** @type {HTMLElement} */ (e.target)?.closest?.('.swatch-variant');
            const imageUrl = li?.dataset?.image;
            
            if (!imageUrl) return;

            const img = this.#findCardImage();

            if (!img) return;

            if (!img.dataset.originalSrc) {
                img.dataset.originalSrc = img.src;
                img.dataset.originalSrcset = img.srcset;
            }
            img.src = imageUrl;
            img.srcset = imageUrl;
        }, { signal, capture: true });

        swatchesList.addEventListener('mouseleave', () => {
            const img = this.#findCardImage();
            if (!img || !img.dataset.originalSrc) return;

            img.src = img.dataset.originalSrc;
            img.srcset = img.dataset.originalSrcset ?? '';
            delete img.dataset.originalSrc;
            delete img.dataset.originalSrcset;
        }, { signal });
    }

    #bindEvents() {
        const swatchesList = /** @type {HTMLElement | undefined} */ (this.refs.swatches);
        if (!swatchesList) return;

        const signal = /** @type {AbortController} */ (this.#abortController).signal;

        swatchesList.addEventListener('scroll', () => this.#updateScrollFade(), { signal, passive: true });

        if (this.#isCard) {
            this.#bindHoverEvents();
            return;
        }

        swatchesList.addEventListener('click', (event) => {
            const button = /** @type {HTMLElement | null} */ (event.target instanceof Element
                ? event.target.closest('[data-action="expand-swatches"]')
                : null);

            if (!button) return;

            event.preventDefault();

            swatchesList
                .querySelectorAll('.swatch-variant--hidden')
                .forEach((item) => item.classList.remove('swatch-variant--hidden'));

            button.remove();

            this.#updateScrollFade();
        }, { signal });
    }

    #showMoreButtonHtml() {
        const label = this.dataset.showMoreLabel || "Show more";

        return `
            <li class="swatch-variant-more-item" role="listitem">
                <button type="button" class="swatch-variant-more" data-action="expand-swatches">${label}</button>
            </li>
        `;
    }

    /** @param {SwatchProduct} item */
    static #isWhiteSwatch(item) {
        if (item.product_color_is_white === true) return true;

        const label = String(item.product_color_label || "").trim().toLowerCase();
        if (label.includes("white")) return true;

        const color = String(item.product_color_hex || "").trim().toLowerCase().replace(/\s+/g, "");
        if (["white", "#fff", "#ffffff", "fff", "ffffff"].includes(color)) return true;

        const rgb = color.match(/^rgba?\((\d+),(\d+),(\d+)/);
        if (!rgb) return false;

        return rgb.slice(1, 4).every((value) => Number(value) >= 250);
    }

    /**
     * @param {SwatchProduct} item
     * @param {string} [additionalClass]
     */
    #swatchHtml(item, additionalClass = "") {
        const background = item.product_color_hex || "transparent";
        const swatchImage = !this.#isCard && item.product_image ? item.product_image : "";
        const imageClass = swatchImage ? " swatch-variant__color--image" : "";
        const imageVariantClass = swatchImage ? " swatch-variant--image" : "";
        const whiteClass = !swatchImage && ColorSwatches.#isWhiteSwatch(item) ? " swatch-variant__color--white" : "";
        const isActive = additionalClass.includes(" active");
        const label = item.product_color_label || item.color_code_current || '';
        const ariaCurrent = isActive ? ' aria-current="true"' : '';
        const swatchStyle = swatchImage
            ? `background-image: url('${swatchImage}'); background-color: ${background};`
            : `--swatch-background: ${background};`;

        const productIdAttr = item.id != null ? ` data-product-id="${item.id}"` : '';
        return `
            <li class="swatch-variant${imageVariantClass}${additionalClass}" role="listitem" data-image="${item.product_model_image}">
                <a href="${item.url}" class="swatch-variant__link" aria-label="${label}"${ariaCurrent}${productIdAttr}>
                    <span class="swatch-variant__color${imageClass}${whiteClass}" style="${swatchStyle}" aria-hidden="true"></span>
                </a>
            </li>
        `;
    }
}

const preloadedProducts = ColorSwatchesStore.getProducts();
if (preloadedProducts instanceof Promise) {
    preloadedProducts.catch((error) => {
        console.error("[ColorSwatches] Failed to preload swatch data:", error);
    });
}

if (!customElements.get("color-swatches-component")) {
    customElements.define("color-swatches-component", ColorSwatches);
}
